/**
 * Klaviyo Integration OAuth Utilities (Story 12.7, Task 2)
 *
 * Handles Klaviyo OAuth 2.0 + PKCE URL building, token exchange, refresh, and error parsing.
 *
 * Key differences from Mailchimp:
 * - Full OAuth 2.0 with PKCE (code_challenge_method=S256)
 * - Scopes required: accounts:read, metrics:read, campaigns:read, flows:read, lists:read, segments:read
 * - Refresh tokens supported (access tokens expire)
 * - Auth header: "Bearer {token}" for all API calls
 * - API revision header required: "revision: 2026-01-15"
 * - Token exchange: application/x-www-form-urlencoded
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const KLAVIYO_AUTH_URL = "https://www.klaviyo.com/oauth/authorize";
const KLAVIYO_TOKEN_URL = "https://a.klaviyo.com/oauth/token";
const TOKEN_EXCHANGE_TIMEOUT_MS = 25_000;

/**
 * OAuth scopes requested for every Klaviyo app install.
 *
 * Exported (Story 31.8 AC #2) as the SINGLE source of truth for
 * `AccountInfo.scopes` in `discoverAccounts()` — do NOT duplicate this
 * literal elsewhere (scope-drift hazard; see the OAuth integration
 * directive's 6-place-drift history). Display/consent metadata only,
 * never access control.
 */
export const KLAVIYO_SCOPES = [
  "accounts:read",
  "metrics:read",
  "campaigns:read",
  "flows:read",
  "lists:read",
  "segments:read",
] as const;

// ============================================================================
// Types
// ============================================================================

export interface KlaviyoAuthUrlOptions {
  clientId: string;
  redirectUri: string;
  /** KV state key to embed in state param */
  stateKey: string;
  /** PKCE code_challenge (Base64URL of SHA-256 of code_verifier) */
  codeChallenge: string;
}

export interface KlaviyoTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

export interface KlaviyoCallbackError {
  code: string;
  userMessage: string;
  userAction: "retry" | "none";
}

// ============================================================================
// Build Authorization URL (Task 2.5)
// ============================================================================

/**
 * Builds the Klaviyo OAuth 2.0 authorization URL with PKCE.
 */
export function buildKlaviyoAuthUrl(options: KlaviyoAuthUrlOptions): string {
  const { clientId, redirectUri, stateKey, codeChallenge } = options;

  if (!clientId) {
    throw new Error("KLAVIYO_CLIENT_ID is required");
  }
  if (!redirectUri) {
    throw new Error("KLAVIYO_OAUTH_REDIRECT_URI is required");
  }
  if (!stateKey) {
    throw new Error("stateKey is required for CSRF protection");
  }
  if (!codeChallenge) {
    throw new Error("codeChallenge is required for PKCE");
  }

  const url = new URL(KLAVIYO_AUTH_URL);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", stateKey);
  url.searchParams.set("scope", KLAVIYO_SCOPES.join(" "));
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", codeChallenge);

  return url.toString();
}

// ============================================================================
// Token Exchange (Task 2.5)
// ============================================================================

/**
 * Exchanges an authorization code for access + refresh tokens with PKCE code_verifier.
 *
 * CRITICAL: Content-Type MUST be application/x-www-form-urlencoded.
 *
 * @returns The parsed token response
 * @throws Error if the exchange fails
 */
export async function exchangeKlaviyoCode(
  code: string,
  codeVerifier: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string
): Promise<KlaviyoTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    client_secret: clientSecret,
    redirect_uri: redirectUri,
    code,
    code_verifier: codeVerifier,
  });

  const result = await safeFetch(
    KLAVIYO_TOKEN_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    throw new Error(
      sanitizeErrorMessage(`Klaviyo token exchange failed: ${result.error.message}`)
    );
  }

  const response = result.data;
  if (!response.ok) {
    let errorMessage = `Klaviyo token exchange failed: HTTP ${response.status}`;
    try {
      const errorBody = (await response.json()) as {
        error?: string;
        error_description?: string;
      };
      if (errorBody.error_description) {
        errorMessage = `Klaviyo token exchange failed: ${errorBody.error_description}`;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(sanitizeErrorMessage(errorMessage));
  }

  const data = (await response.json()) as KlaviyoTokenResponse;

  if (!data.access_token) {
    throw new Error("Invalid Klaviyo token response: missing access_token");
  }
  if (!data.refresh_token) {
    throw new Error("Invalid Klaviyo token response: missing refresh_token");
  }

  return data;
}

// ============================================================================
// Token Refresh (Task 2.5)
// ============================================================================

/**
 * Refreshes a Klaviyo access token using the refresh token.
 *
 * @returns New token response with fresh access_token and refresh_token
 * @throws Error if the refresh fails
 */
export async function refreshKlaviyoToken(
  refreshToken: string,
  clientId: string,
  clientSecret: string
): Promise<KlaviyoTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: clientId,
    client_secret: clientSecret,
    refresh_token: refreshToken,
  });

  const result = await safeFetch(
    KLAVIYO_TOKEN_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    throw new Error(
      sanitizeErrorMessage(`Klaviyo token refresh failed: ${result.error.message}`)
    );
  }

  const response = result.data;
  if (!response.ok) {
    let errorMessage = `Klaviyo token refresh failed: HTTP ${response.status}`;
    try {
      const errorBody = (await response.json()) as {
        error?: string;
        error_description?: string;
      };
      if (errorBody.error_description) {
        errorMessage = `Klaviyo token refresh failed: ${errorBody.error_description}`;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(sanitizeErrorMessage(errorMessage));
  }

  const data = (await response.json()) as KlaviyoTokenResponse;

  if (!data.access_token) {
    throw new Error("Invalid Klaviyo refresh response: missing access_token");
  }

  return data;
}

// ============================================================================
// Callback Error Parsing (Task 2.5)
// ============================================================================

/**
 * Parses Klaviyo OAuth callback errors from query params.
 */
export function parseKlaviyoCallbackError(
  searchParams: URLSearchParams
): KlaviyoCallbackError | null {
  const error = searchParams.get("error");
  if (!error) return null;

  if (error === "access_denied") {
    return {
      code: "oauth-denied",
      userMessage: "You denied the Klaviyo connection request.",
      userAction: "retry",
    };
  }

  if (error === "invalid_request" || error === "redirect_uri_mismatch") {
    return {
      code: "oauth-config-error",
      userMessage: "Klaviyo connection is misconfigured. Contact support.",
      userAction: "none",
    };
  }

  if (error === "server_error") {
    return {
      code: "oauth-error",
      userMessage: "Klaviyo is temporarily unavailable. Please try again later.",
      userAction: "retry",
    };
  }

  return {
    code: "oauth-error",
    userMessage: "An error occurred connecting to Klaviyo. Please try again.",
    userAction: "retry",
  };
}
